import React, { useCallback, useEffect, useRef, useState } from "react";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { toPng } from "html-to-image";
import { markScreenshotNotReady, signalScreenshotSceneReady } from "../../../lib/screenshotReady";
import ScreenshotExpiredPage from "../components/ScreenshotExpiredPage";
import { isScreenshotAccessExpired } from "../lib/screenshotAccess";

const SAVE_BAR_H = 64;

/**
 * Shared save/preview shell for app-icon capture pages.
 * Children render inside a `size`×`size` frame that is exported verbatim as PNG.
 *
 * @param {boolean} [transparent] — export with an alpha channel (no white fill). Facebook rejects opaque white.
 */
export default function IconCaptureCanvas({
    routePath,
    slug,
    fileName,
    frameId,
    size = 512,
    ready = true,
    transparent = false,
    children,
}) {
    const [previewScale, setPreviewScale] = useState(1);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const frameRef = useRef(null);

    const expired = isScreenshotAccessExpired();

    useEffect(() => {
        if (expired || !ready) {
            markScreenshotNotReady();
            return undefined;
        }
        signalScreenshotSceneReady({ path: routePath, slug });
        return undefined;
    }, [expired, ready, routePath, slug]);

    useEffect(() => {
        const updateScale = () => {
            const padding = 32;
            const availableW = window.innerWidth - padding;
            const availableH = window.innerHeight - SAVE_BAR_H - padding;
            setPreviewScale(Math.min(1, availableW / size, availableH / size));
        };
        updateScale();
        window.addEventListener("resize", updateScale);
        return () => window.removeEventListener("resize", updateScale);
    }, [size]);

    useEffect(() => {
        const prevHtml = document.documentElement.style.overflow;
        const prevBody = document.body.style.overflow;
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        return () => {
            document.documentElement.style.overflow = prevHtml;
            document.body.style.overflow = prevBody;
        };
    }, []);

    const handleSave = useCallback(async () => {
        const node = frameRef.current;
        if (!node || saving) return;

        setSaving(true);
        setSaveError(null);
        const previousTransform = node.style.transform;
        node.style.transform = "none";

        try {
            await new Promise((resolve) =>
                requestAnimationFrame(() => requestAnimationFrame(resolve)),
            );
            const dataUrl = await toPng(node, {
                width: size,
                height: size,
                pixelRatio: 1,
                cacheBust: true,
                skipFonts: true,
                ...(transparent ? { backgroundColor: null } : {}),
                style: {
                    transform: "none",
                    transformOrigin: "top left",
                    ...(transparent ? { background: "transparent" } : {}),
                },
            });
            const link = document.createElement("a");
            link.download = fileName;
            link.href = dataUrl;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (e) {
            setSaveError(e?.message || "Failed to save image");
        } finally {
            node.style.transform = previousTransform;
            setSaving(false);
        }
    }, [saving, size, fileName, transparent]);

    if (expired) {
        return <ScreenshotExpiredPage />;
    }

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#020617",
                overflow: "hidden",
                zIndex: 1,
                gap: 1.5,
                pb: `${SAVE_BAR_H}px`,
                boxSizing: "border-box",
            }}
        >
            <Box
                sx={{
                    width: Math.round(size * previewScale),
                    height: Math.round(size * previewScale),
                    position: "relative",
                    overflow: "hidden",
                    flexShrink: 0,
                    ...(transparent
                        ? {
                            background:
                                "repeating-conic-gradient(#94a3b8 0% 25%, #e2e8f0 0% 50%) 50% / 24px 24px",
                        }
                        : {}),
                }}
            >
                <Box
                    ref={frameRef}
                    data-screenshot-frame={frameId}
                    sx={{
                        width: size,
                        height: size,
                        transform: `scale(${previewScale})`,
                        transformOrigin: "top left",
                        position: "relative",
                        overflow: "hidden",
                        borderRadius: 0,
                        lineHeight: 0,
                        bgcolor: transparent ? "transparent" : undefined,
                    }}
                >
                    {children}
                </Box>
            </Box>

            <Box
                sx={{
                    position: "fixed",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: SAVE_BAR_H,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    bgcolor: "rgba(2, 6, 23, 0.92)",
                    borderTop: "1px solid rgba(148, 163, 184, 0.2)",
                    zIndex: 20,
                    px: 2,
                    boxSizing: "border-box",
                }}
            >
                <Button
                    variant="contained"
                    size="medium"
                    startIcon={
                        saving ? <CircularProgress size={16} color="inherit" /> : <SaveAltIcon />
                    }
                    onClick={handleSave}
                    disabled={!ready || saving}
                    sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        px: 3,
                        bgcolor: "#0ea5e9",
                        "&:hover": { bgcolor: "#0284c7" },
                    }}
                >
                    {saving ? "Saving…" : `Save ${size}×${size} PNG`}
                </Button>
                {transparent ? (
                    <Typography variant="caption" sx={{ color: "rgba(226,232,240,0.72)", lineHeight: 1.2 }}>
                        Transparent background (Facebook)
                    </Typography>
                ) : null}
                {saveError ? (
                    <Typography variant="caption" color="error" sx={{ lineHeight: 1.2 }}>
                        {saveError}
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
}

IconCaptureCanvas.propTypes = {
    routePath: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    fileName: PropTypes.string.isRequired,
    frameId: PropTypes.string.isRequired,
    size: PropTypes.number,
    ready: PropTypes.bool,
    transparent: PropTypes.bool,
    children: PropTypes.node,
};
